import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD309Query } from './hooks/useD309Query';
import type { ReportRow, UnitSummary } from '../../services/d309Api';
import './D309Form.css';

const D309Form: React.FC = () => {
  const { loading, data, summary, groupCounts, queried, noData, runQuery, reset } = useD309Query();

  const [reportType, setReportType] = useState<string>('vendor_date');
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [orderNoFrom, setOrderNoFrom] = useState('');
  const [orderNoTo, setOrderNoTo] = useState('');
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [undeliveredOnly, setUndeliveredOnly] = useState(false);

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      vendor_from: vendorFrom.trim() || undefined,
      vendor_to: vendorTo.trim() || undefined,
      order_no_from: orderNoFrom.trim() || undefined,
      order_no_to: orderNoTo.trim() || undefined,
      delivery_date_from: deliveryDateFrom.trim() || undefined,
      delivery_date_to: deliveryDateTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      undelivered_only: undeliveredOnly || undefined,
    });
  };

  const handleReset = () => {
    setReportType('vendor_date');
    setVendorFrom('');
    setVendorTo('');
    setOrderNoFrom('');
    setOrderNoTo('');
    setDeliveryDateFrom('');
    setDeliveryDateTo('');
    setProductFrom('');
    setProductTo('');
    setUndeliveredOnly(false);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const title = reportType === 'order_vendor' ? '採購預計交期明細（單據廠商別）' : '採購預計交期明細（廠商交期別）';
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>廠商代號</th><th>廠商名稱</th><th>預計交期</th><th>單據號碼</th><th>產品代號</th><th>單價</th><th>採購數量</th><th>已繳數量</th><th>未繳數量</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD110_12}</td><td>${r.vendor_name}</td><td>${r.FD111_19}</td><td>${r.FD110_01}</td><td>${r.product_code}</td><td class="right">${r.FD111_17?.toFixed(2)}</td><td class="right">${r.order_qty}</td><td class="right">${r.delivered_qty}</td><td class="right">${r.undeliv_qty}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '廠商代號', dataIndex: 'FD110_12', key: 'FD110_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '預計交期', dataIndex: 'FD111_19', key: 'FD111_19', width: 100 },
    { title: '單據號碼', dataIndex: 'FD110_01', key: 'FD110_01', width: 110 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '單價', dataIndex: 'FD111_17', key: 'FD111_17', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '採購數量', dataIndex: 'order_qty', key: 'order_qty', width: 90, align: 'right' as const },
    { title: '已繳數量', dataIndex: 'delivered_qty', key: 'delivered_qty', width: 90, align: 'right' as const },
    { title: '未繳數量', dataIndex: 'undeliv_qty', key: 'undeliv_qty', width: 90, align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 0 ? '#f5222d' : undefined }}>{v}</span>,
    },
  ];

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '項目', dataIndex: 'label', key: 'label', width: 150 },
    { title: '數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D309} action="query">
      <div className="d309-container">
        <div className="d309-header">
          <h2>採購預計交期明細</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="vendor_date">廠商交期別</Radio.Button>
              <Radio.Button value="order_vendor">單據廠商別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('廠商代號起')}
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號迄')}
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('單號起')}
              <Input value={orderNoFrom} onChange={e => setOrderNoFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('單號迄')}
              <Input value={orderNoTo} onChange={e => setOrderNoTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('預計交期起 (YYYY-MM-DD)')}
              <Input value={deliveryDateFrom} onChange={e => setDeliveryDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('預計交期迄 (YYYY-MM-DD)')}
              <Input value={deliveryDateTo} onChange={e => setDeliveryDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ paddingTop: 18 }}>
              <Checkbox checked={undeliveredOnly} onChange={e => setUndeliveredOnly(e.target.checked)}>
                只顯示未繳庫
              </Checkbox>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                廠商筆數：{groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FD110_01}-${r.product_code}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }}
              style={{ marginBottom: 16 }}
            />
            {summary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={summary}
                  rowKey="label"
                  size="small"
                  pagination={false}
                  scroll={{ x: 300 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D309Form;
